import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPassword() {
  const [, navigate] = useLocation();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "invalid">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    const e = params.get("email");
    if (!t) { setStatus("invalid"); return; }
    setToken(t);
    if (e) setEmail(decodeURIComponent(e));
  }, []);

  const handleSubmit = async () => {
    if (!password || password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!/[A-Z]/.test(password)) { setError("Password must contain at least one uppercase letter."); return; }
    if (!/[0-9]/.test(password)) { setError("Password must contain at least one number."); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setError("Password must contain at least one special character."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setStatus("loading");
    setError("");
    try {
      const res = await fetch("/api/v1/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setError(data.message ?? "Reset failed. Please request a new link.");
      }
    } catch {
      setStatus("error");
      setError("Network error. Please try again.");
    }
  };

  if (status === "invalid") {
    return (
      <div className="min-h-screen bg-[#12122A] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1B1B3D] rounded-2xl border border-white/10 p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invalid link</h1>
          <p className="text-white/60 text-sm mb-6">This reset link is invalid or has expired. Please request a new one.</p>
          <Button onClick={() => navigate("/forgot-password")} className="w-full rounded-xl font-bold">Request New Link</Button>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-[#12122A] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1B1B3D] rounded-2xl border border-white/10 p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Password reset!</h1>
          <p className="text-white/60 text-sm mb-6">Your password has been updated successfully. You can now log in with your new password.</p>
          <Button onClick={() => navigate("/login")} className="w-full rounded-xl font-bold">Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#12122A] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1B1B3D] rounded-2xl border border-white/10 p-8 shadow-2xl">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">Reset your password</h1>
        <p className="text-white/60 text-sm mb-1">Choose a strong new password for your CommonQR account.</p>
        {email && <p className="text-violet-400 text-xs font-medium mb-5">{email}</p>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1.5">New Password</label>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-violet-500"
            />
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {[
                { label: "6+ characters", met: password.length >= 6 },
                { label: "1 uppercase letter", met: /[A-Z]/.test(password) },
                { label: "1 number", met: /[0-9]/.test(password) },
                { label: "1 special character", met: /[^A-Za-z0-9]/.test(password) },
              ].map((req) => (
                <div key={req.label} className={"flex items-center gap-1.5 text-xs " + (password.length === 0 ? "text-white/30" : req.met ? "text-green-400" : "text-red-400")}>
                  <span>{req.met && password.length > 0 ? "✓" : "○"}</span>
                  {req.label}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1.5">Confirm Password</label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your new password"
              className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-violet-500"
            />
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <Button
            type="button"
            onClick={handleSubmit as any}
            disabled={status === "loading"}
            className="w-full rounded-xl font-bold py-2.5"
          >
            {status === "loading" ? "Resetting…" : "Reset Password"}
          </Button>
          <button type="button" onClick={() => navigate("/login")} className="w-full text-sm text-white/50 hover:text-white/80 transition-colors">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
